/**
 * <lih-video> — click-to-play video facade for the Live In Hoops landing page.
 *
 * Markup contract:
 *   <lih-video data-lih-embed="https://...">   (data-lih-embed optional)
 *     <div data-lih-frame> ... <video> or nothing ... </div>
 *     <img data-lih-poster>            (optional)
 *     <span data-lih-overlay></span>   (optional)
 *     <button data-lih-trigger></button>
 *   </lih-video>
 *
 * Nothing is requested until the visitor clicks: a Shopify-hosted <video> is
 * preload="none", and a YouTube/Vimeo iframe is only created on click. That
 * keeps the third-party player entirely off the initial page load.
 *
 * On play the host gets data-playing="true"; CSS hides the poster, tint and
 * button from there.
 */
class LihVideo extends HTMLElement {
  connectedCallback() {
    this.autoplay = this.hasAttribute('data-lih-autoplay');
    this.trigger = this.querySelector('[data-lih-trigger]');
    if (!this.trigger && !this.autoplay) return;

    /* The markup ships with `controls` so the video is still operable if this
       script never runs. Once it has, the custom play button takes over and the
       native bar is stripped until playback actually starts — it otherwise sits
       on top of the poster, competing with the play button. */
    const native = this.querySelector('video');
    if (native) {
      native.removeAttribute('controls');

      /* Mirror the player's own state rather than tracking it here: pausing
         from the native bar, the keyboard or picture-in-picture all land on
         the same events, so the button can never disagree with the video. */
      native.addEventListener('play', () => this.setPaused(false));
      native.addEventListener('pause', () => this.setPaused(true));
      native.addEventListener('ended', () => this.setPaused(true));
    }

    /* The markup already carries data-playing, so the facade is down. Start
       the player here anyway: the attribute alone gives nothing to catch, and
       a refusal — low power mode, for one — has to put the button back or
       there is no way left to start it. */
    if (this.autoplay && native) {
      const started = native.play();
      if (started && typeof started.catch === 'function') {
        started.catch(() => this.setAttribute('data-paused', 'true'));
      }
    }

    this.addEventListener('click', (event) => {
      /* While it autoplays there is no button on screen to press, so the
         frame itself is the pause control. */
      const press = this.autoplay ? '[data-lih-frame], [data-lih-trigger]' : '[data-lih-trigger]';
      if (!event.target.closest(press)) return;
      event.preventDefault();
      this.toggle();
    });
  }

  /* While a video autoplays the same button is the pause control, so its
     accessible name has to follow the player rather than stay "Play video".
     Both strings come from the markup — they are merchant copy. */
  setPaused(paused) {
    if (paused) this.setAttribute('data-paused', 'true');
    else this.removeAttribute('data-paused');

    const labels = this.trigger?.dataset;
    const next = paused ? labels?.lihPlayLabel : labels?.lihPauseLabel;
    if (next) this.trigger.setAttribute('aria-label', next);
  }

  /* Pause only ever applies to a running native player — an embed owns its
     own controls, and a facade that has not started has nothing to stop. */
  toggle() {
    const native = this.querySelector('video');

    /* From the first press the visitor owns playback, and the autoplay
       attribute has to go with it. A muted autoplay video stays under the
       browser's own visibility management, which resumes it when the element
       comes back into view — and the smallest scroll, or the layout shift from
       the button appearing, is enough to trigger that. The pause lands, the
       browser undoes it, and it reads as a press that did not take. */
    native?.removeAttribute('autoplay');

    if (native && !native.paused) {
      native.pause();
      return;
    }
    this.play();
  }

  play() {
    const native = this.querySelector('video');
    const embed = this.dataset.lihEmbed;

    if (native) {
      /* First press only: drop the poster and hand back the native bar. Later
         presses are resumes, so this must not run again. */
      if (!this.hasAttribute('data-playing')) {
        this.setAttribute('data-playing', 'true');
        /* Restored before play() so the bar is present whichever way this goes
           — including when the browser refuses playback and the visitor has to
           start it themselves. */
        native.setAttribute('controls', 'controls');
      }
      /* The button the visitor just activated is now hidden, so focus would be
         lost to the top of the document. Move it onto the player instead. */
      native.focus({ preventScroll: true });
      const started = native.play();
      /* Autoplay can be refused (low power mode, for one). The controls are
         already visible by then, so the visitor can still start it. */
      if (started && typeof started.catch === 'function') started.catch(() => {});
      return;
    }

    /* An embedded player runs in a cross-origin iframe, so there is no pause
       to listen for — it is injected once and owns its own controls. */
    if (this.hasAttribute('data-playing')) return;

    if (embed) {
      const frame = this.querySelector('[data-lih-frame]');
      if (!frame) return;

      const iframe = document.createElement('iframe');
      iframe.src = embed;
      iframe.title = this.dataset.lihTitle || 'Video';
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      iframe.loading = 'lazy';
      iframe.frameBorder = '0';

      frame.appendChild(iframe);
      this.setAttribute('data-playing', 'true');
      iframe.focus({ preventScroll: true });
    }
  }
}

if (!customElements.get('lih-video')) {
  customElements.define('lih-video', LihVideo);
}
